import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { computed, ref } from 'vue';
import { Learner, LearnerWithInstructorNotes } from '../models/learner';

/**
 * Learner assign validation composable.
 * Handle field input, validation and more.
 * @param {import('vue').Ref<boolean> | undefined} [isCourseRequiredDateOfBirth] Whether course required date of birth.
 */
export function useLearnerAssignValidation(isCourseRequiredDateOfBirth) {
  const validators = ref([]);
  const $externalResults = ref({});

  /** @type {import('vue').Ref<LearnerWithInstructorNotes[]>} */
  const clonedLearnersList = ref([]);

  const rules = computed(() => {
    let validationFields = {};
    if (isCourseRequiredDateOfBirth?.value) {
      validationFields = {
        ...validationFields,
        dateOfBirth: {
          required,
        },
      };
    }
    return validationFields;
  });

  /**
   * Create validators for selected learners.
   * @param {Learner[]} newSelectedLearners New selected learners.
   */
  function createValidatorForClonedSelectedLearners(newSelectedLearners) {
  // Keep in mind that `useVuelidate` only work with reactive object or array, do not pass a plain object without reactivity
  // into this function.
    const newValidators = newSelectedLearners.map(learner => ({
      id: learner.id,
      validator: useVuelidate(
        rules,
        learner,
        { $externalResults },
      ),
    }));
    validators.value = newValidators;
  }

  /**
   * Get validator for fields.
   * @param {string} key Key of field.
   * @param {number} id Validator ID.
   * @returns {object | null}
   */
  function getValidatorsForField(key, id) {
    const validator = validators.value.find(v => v.id === id);
    if (validator == null) {
      return null;
    }
    const validatorForField = validator.validator[key];
    return {
      errors: validatorForField?.$errors.map(e => e.$message),
      touch: validatorForField?.$touch,
    };
  }

  /**
   * Validate form.
   * @param {Learner["id"]} learnerIdToValidate Learner ID to validate.
   * @param {Function} callbackExecute Callback execute.
   */
  async function validateFormAndExecute(learnerIdToValidate, callbackExecute) {
    const learnerValidator = validators.value.find(v => v.id === learnerIdToValidate);
    learnerValidator.validator.$touch();
    const isFormInvalid = learnerValidator.validator.$invalid;
    if (isFormInvalid) {
      return;
    }
    callbackExecute();
  }

  return {
    validateFormAndExecute,
    clonedLearnersList,
    createValidatorForClonedSelectedLearners,
    getValidatorsForField,
  };
}
